import { DocumentSnapshot, Timestamp, logger } from "../firestoreImports"
import { z } from "zod"

const companySchema = z.object({
    title: z.string().nullish(),
    signUpCodes: z.string().array().default([]),
    signUpDomains: z.string().array().default([]),
    locations: z.string().array().default([]),
    passwordEnabled: z.boolean().default(true),
    carpoolEnabled: z.boolean().default(false),
    garageEnabled: z.boolean().default(true),
    ssoProviderId: z.string().nullish(),
    eventDates: z.array(z.custom<Timestamp>()).nullish().default([]),
    eventStartDate: z.custom<Timestamp>().nullish(),
    eventEndDate: z.custom<Timestamp>().nullish()
})

interface ICompany {
    id: string
    title?: string
    signUpCodes: string[]
    signUpDomains: string[]
    locations: string[]
    passwordEnabled: boolean
    carpoolEnabled: boolean
    garageEnabled: boolean
    ssoProviderId?: string
    eventDates?: Date[]
    eventStartDate?: Date
    eventEndDate?: Date
}

export interface Company extends ICompany {}
export class Company {
    constructor(params: ICompany) {
        Object.assign(this, params)
    }

    static fromData(doc: DocumentSnapshot): Company | null {
        const raw = doc.data()
        if (!raw) {
            logger.warn(`Cannot create Company ${doc.id} from snapshot: missing data`)
            return null
        }

        try {
            const parsed = companySchema.parse(raw)
            const title = parsed.title ?? doc.id
            return new Company({
                id: doc.id,
                title: title,
                signUpCodes: parsed.signUpCodes,
                signUpDomains: parsed.signUpDomains,
                locations: parsed.locations,
                passwordEnabled: parsed.passwordEnabled,
                carpoolEnabled: parsed.carpoolEnabled,
                garageEnabled: parsed.garageEnabled,
                ssoProviderId: parsed.ssoProviderId ?? undefined,
                eventDates: parsed.eventDates?.map(it => it.toDate()) ?? undefined,
                eventStartDate: parsed.eventStartDate?.toDate() ?? undefined,
                eventEndDate: parsed.eventEndDate?.toDate() ?? undefined
            })
        } catch (error) {
            logger.error(`Cannot create Company ${doc.id} from snapshot:`, error)
            return null
        }
    }

    asData(): { [k: string]: any } {
        return companySchema.parse({
            title: this.title,
            signUpCodes: this.signUpCodes,
            signUpDomains: this.signUpDomains,
            locations: this.locations,
            passwordEnabled: this.passwordEnabled,
            carpoolEnabled: this.carpoolEnabled,
            garageEnabled: this.garageEnabled,
            ssoProviderId: this.ssoProviderId,
            eventDates: this.eventDates,
            eventStartDate: this.eventStartDate,
            eventEndDate: this.eventEndDate
        })
    }

    toString(): string {
        return this.title ? `${this.title} (${this.id})` : this.id
    }
}
